import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { parsePhoneNumber, AsYouType, getCountries, CountryCode, formatIncompletePhoneNumber } from 'libphonenumber-js';
import { getCountryCallingCode } from 'libphonenumber-js';
import { TranslateService } from '@ngx-translate/core';
import { MyAccountService } from 'src/app/core/services/my-account.service';
import { normalizeCountryValue } from 'src/app/core/utils/country.utils';
import { take } from 'rxjs';

@Component({
    selector: 'app-phone-input',
    templateUrl: './phone-input.component.html',
    styleUrls: ['./phone-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneInputComponent),
            multi: true
        }
    ]
})
export class PhoneInputComponent implements OnInit, OnChanges, ControlValueAccessor {
    @Input() label: string = 'common.phoneNumber';
    @Input() required: boolean = false;
    @Input() placeholder: string = 'common.phoneNumberPlaceholder';
    @Input() errorMessage: string = 'common.phoneNumberTooShort';
    @Input() defaultCountry: CountryCode = 'ES';
    @Input() inputId: string = 'phoneNumber';
    @Input() inputName: string = 'phoneNumber';
    @Input() dropdownPlaceholder: string = 'common.selectCountry';
    @Input() minLength: number = 8;
    @Input() hideLabel: boolean = false;

    @Output() phoneChange = new EventEmitter<{
        number: string,
        prefix: string,
        fullNumber: string,
        e164Number: string,
        valid: boolean
    }>();

    countries: { code: CountryCode, name: string }[] = [];
    selectedCountry: CountryCode | null = null;
    phonePrefix: string = '';
    phoneNumber: string = '';
    disabled: boolean = false;
    showError: boolean = false;

    onChange: any = () => { };
    onTouched: any = () => { };

    constructor(
        private translate: TranslateService,
        private myAccountService: MyAccountService
    ) { }

    ngOnInit(): void {
        this.initializeCountries();

        if (this.defaultCountry) {
            this.selectedCountry = this.defaultCountry;
            this.updatePhonePrefix(this.defaultCountry);
        }

        this.initializeDefaultCountryFromCenter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes['defaultCountry'];
        if (!change?.currentValue) return;

        const nextCountry = change.currentValue as CountryCode;
        const prevCountry = change.previousValue as CountryCode | undefined;

        if (this.phoneNumber && this.phoneNumber.length > 0) return;
        if (this.selectedCountry && prevCountry && this.selectedCountry !== prevCountry) return;

        this.selectedCountry = nextCountry;
        this.updatePhonePrefix(nextCountry);
    }

    initializeCountries(): void {
        const lang = this.translate.currentLang || 'en';

        const preferredCountryCodes = ['PT', 'ES', 'GB', 'FR', 'DE', 'IT', 'US', 'CL', 'TW'];
        const preferredCountries: { code: CountryCode, name: string }[] = [];
        const otherCountries: { code: CountryCode, name: string }[] = [];

        const allCountryCodes = getCountries();
        allCountryCodes.forEach(code => {
            const countryObj = {
                code: code as CountryCode,
                name: new Intl.DisplayNames([lang], { type: 'region' }).of(code) || code
            };

            if (preferredCountryCodes.includes(code)) {
                preferredCountries.push(countryObj);
            } else {
                otherCountries.push(countryObj);
            }
        });

        preferredCountries.sort((a, b) => a.name.localeCompare(b.name));
        otherCountries.sort((a, b) => a.name.localeCompare(b.name));

        this.countries = [...preferredCountries, ...otherCountries];
    }

    private initializeDefaultCountryFromCenter(): void {
        this.myAccountService.getCenterInfo()
            .pipe(take(1))
            .subscribe({
                next: (center) => {
                    const resolvedCountry = this.resolveCountryCodeFromCenter(center?.country);
                    if (!resolvedCountry) {
                        return;
                    }

                    if (this.phoneNumber && this.phoneNumber.length > 0) {
                        return;
                    }

                    this.defaultCountry = resolvedCountry;
                    this.selectedCountry = resolvedCountry;
                    this.updatePhonePrefix(resolvedCountry);
                },
                error: () => {
                    // Si falla la carga del centro, mantenemos el comportamiento por defecto
                }
            });
    }

    private resolveCountryCodeFromCenter(centerCountry: unknown): CountryCode | null {
        const normalized = normalizeCountryValue(String(centerCountry ?? ''));
        if (!normalized) return null;

        const map: Record<string, CountryCode> = {
            PT: 'PT',
            PRT: 'PT',
            PORTUGAL: 'PT',
            ES: 'ES',
            ESP: 'ES',
            SPAIN: 'ES',
            ESPANA: 'ES',
            FR: 'FR',
            FRA: 'FR',
            FRANCE: 'FR',
            GB: 'GB',
            GBR: 'GB',
            UK: 'GB',
            IT: 'IT',
            DE: 'DE',
            US: 'US',
            CL: 'CL',
            TW: 'TW',
            TAIWAN: 'TW',
        };

        return map[normalized] ?? null;
    }

    getCountryCallingCode(countryCode: CountryCode): string {
        return getCountryCallingCode(countryCode);
    }

    onCountryChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const countryCode = select.value;

        if (!countryCode || countryCode === '') {
            this.selectedCountry = null;
            this.phonePrefix = '';
        } else {
            this.selectedCountry = countryCode as CountryCode;
            this.updatePhonePrefix(this.selectedCountry);
        }

        this.emitChange();
    }

    updatePhonePrefix(countryCode: CountryCode): void {
        try {
            this.phonePrefix = '+' + this.getCountryCallingCode(countryCode);
        } catch (error) {
            console.error('Error updating phone prefix', error);
        }
    }

    formatPhoneNumber(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.phoneNumber = input.value;

        if (this.selectedCountry) {
            try {
                const formatter = new AsYouType(this.selectedCountry);
                input.value = formatter.input(this.phoneNumber);
                this.phoneNumber = input.value;
            } catch (error) {
                console.error('Error formatting phone number', error);
            }
        }

        this.emitChange();
    }

    isValid(): boolean {
        return (this.phoneNumber && this.phoneNumber.length >= this.minLength) || !this.phoneNumber;
    }

    emitChange(): void {
        this.onChange(this.getE164Number());

        const fullNumber = this.phonePrefix ? this.phonePrefix + ' ' + this.phoneNumber : this.phoneNumber;
        const isValid = this.isValid();
        const e164Number = this.getE164Number();

        this.phoneChange.emit({
            number: this.phoneNumber,
            prefix: this.phonePrefix,
            fullNumber: fullNumber,
            e164Number: e164Number,
            valid: isValid
        });
    }

    getE164Number(): string {
        if (!this.phoneNumber || !this.selectedCountry) {
            return '';
        }

        try {
            const fullNumber = this.phonePrefix + this.phoneNumber;
            const phoneNumber = parsePhoneNumber(fullNumber);

            if (phoneNumber && phoneNumber.isValid()) {
                return phoneNumber.format('E.164');
            }

            const cleanPrefix = this.phonePrefix.startsWith('+') ?
                this.phonePrefix.replace(/\s+/g, '') :
                '+' + this.phonePrefix.replace(/\s+/g, '');

            const cleanNumber = this.phoneNumber.replace(/\D/g, '');

            return cleanPrefix + cleanNumber;
        } catch (error) {
            console.error('Error formatting to E.164', error);

            const cleanPrefix = this.phonePrefix.startsWith('+') ?
                this.phonePrefix.replace(/\s+/g, '') :
                '+' + this.phonePrefix.replace(/\s+/g, '');

            const cleanNumber = this.phoneNumber.replace(/\D/g, '');

            return cleanPrefix + cleanNumber;
        }
    }

    writeValue(value: string): void {
        if (!value) {
            this.phoneNumber = '';
            this.selectedCountry = this.defaultCountry;
            this.updatePhonePrefix(this.defaultCountry);
            return;
        }

        try {
            const phoneNumber = parsePhoneNumber(value);

            if (phoneNumber && phoneNumber.country) {
                this.selectedCountry = phoneNumber.country;
                this.updatePhonePrefix(phoneNumber.country);
                this.phoneNumber = phoneNumber.nationalNumber;
            } else {
                const match = value.match(/^\+(\d+)\s*(.*)$/);
                if (match) {
                    const prefix = '+' + match[1];
                    this.phoneNumber = match[2];

                    for (const country of getCountries()) {
                        if ('+' + getCountryCallingCode(country as CountryCode) === prefix) {
                            this.selectedCountry = country as CountryCode;
                            this.updatePhonePrefix(this.selectedCountry);
                            break;
                        }
                    }
                } else {
                    this.phoneNumber = value;
                    this.selectedCountry = this.defaultCountry;
                    this.updatePhonePrefix(this.defaultCountry);
                }
            }
        } catch (error) {
            console.error('Error parsing phone number', error);
            this.phoneNumber = value;
            this.selectedCountry = this.defaultCountry;
            this.updatePhonePrefix(this.defaultCountry);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = () => {
            this.showError = true;
            fn();
        };
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
